import React, { useState } from 'react'
import { useMarket } from './MarketContext'

/**
 * Shared dark glass card for all marketplace item types.
 * variant: 'cover' (full image), 'badge' (small centered icon), 'font' (type specimen)
 * category: 'profile' | 'background' | 'badge' | 'font' | 'rank' (matches /api/buy)
 */
const MarketCard = ({ value, variant = 'cover', category = 'profile' }) => {
  const { loggedIn, owned, equipped, balance, buy } = useMarket()
  const [buying, setBuying] = useState(false)
  const [error, setError] = useState(null)

  const forSale = Number.isInteger(value.price)
  const isOwned = loggedIn && owned[category]?.has(value.tag)
  const isEquipped = loggedIn && (
    category === 'badge'
      ? equipped.badge?.has?.(value.tag)
      : equipped[category] === value.tag
  )
  const canAfford = balance >= (value.price || 0)

  const onBuy = async () => {
    if (!loggedIn) {
      window.location.href = '/auth/discord'
      return
    }
    setBuying(true)
    setError(null)
    try {
      const result = await buy(category, value.tag)
      if (result && !result.ok) {
        setError(result.error === 'insufficient_balance' ? 'Not enough RP' : 'Purchase failed')
      }
    } catch {
      setError('Purchase failed')
    } finally {
      setBuying(false)
    }
  }

  return (
    <div className={`group glass glass-hover overflow-hidden flex flex-col font-poppins relative ${isOwned ? 'border-emerald-400/30' : ''}`}>
      {/* Ownership ribbon */}
      {isEquipped && (
        <span className="absolute top-3 left-3 z-10 bg-amber-400 text-black text-[10px] font-semibold tracking-[0.15em] uppercase px-2.5 py-1 rounded-full">
          Equipped
        </span>
      )}
      {isOwned && !isEquipped && (
        <span className="absolute top-3 left-3 z-10 bg-emerald-500/90 text-white text-[10px] font-semibold tracking-[0.15em] uppercase px-2.5 py-1 rounded-full">
          Owned
        </span>
      )}

      {variant === 'cover' && (
        <div className="h-44 w-full overflow-hidden bg-gradient-to-br from-reored/10 via-transparent to-reoviolet/10">
          <img
            src={value.url}
            alt={value.name}
            loading="lazy"
            decoding="async"
            onError={(e) => { e.currentTarget.style.display = 'none' }}
            className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}
      {variant === 'badge' && (
        <div className="h-44 w-full flex items-center justify-center bg-gradient-to-br from-white/5 to-transparent">
          <img
            src={value.url}
            alt={value.name}
            loading="lazy"
            decoding="async"
            onError={(e) => { e.currentTarget.style.display = 'none' }}
            className="w-20 h-20 object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-[0_0_15px_rgba(213,60,53,0.3)]"
          />
        </div>
      )}
      {variant === 'font' && (
        <div className="h-44 w-full flex items-center justify-center bg-gradient-to-br from-white/5 to-transparent">
          <span
            className="text-6xl text-white/90 group-hover:scale-110 transition-transform duration-500"
            style={{ fontFamily: value.name }}
          >
            Aa
          </span>
        </div>
      )}

      <div className="p-4 flex-grow">
        <h2 className="font-Bebas text-white text-2xl tracking-wide mb-1">{value.name}</h2>
        <p className="text-white/45 text-sm line-clamp-2">{value.description}</p>
      </div>

      <div className="flex items-center justify-between p-4 pt-0">
        {!forSale ? (
          <span className="text-white/30 text-xs tracking-[0.2em] uppercase border border-white/10 rounded-full px-4 py-1.5">
            Battle Pass Exclusive
          </span>
        ) : isOwned ? (
          <span className="text-emerald-400 text-sm font-medium">✓ In your inventory</span>
        ) : (
          <>
            <p className="text-white/60 text-sm">
              <span className="text-amber-300 font-semibold">{value.price}</span> RP
            </p>
            <div className="flex items-center gap-2">
              {error && <span className="text-reored text-xs">{error}</span>}
              <button
                onClick={onBuy}
                disabled={buying || (loggedIn && !canAfford)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  loggedIn && !canAfford
                    ? 'bg-white/10 text-white/30 cursor-not-allowed'
                    : 'bg-reored hover:bg-red-600 text-white'
                }`}
              >
                {buying ? '…' : loggedIn ? (canAfford ? 'Buy' : 'Low RP') : 'Login to buy'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default MarketCard
